/**
 * Inline shadow-indicator in de topbar (ADR-0020). Geen eigen
 * positionering / achtergrond — de topbar zelf krijgt een
 * `app-topbar--shadow-*` modifier-class die de kleur regelt. Dit
 * component levert alleen de inhoud:
 *
 *  - Heading (welke modus + de naam van de identiteit waaronder je werkt)
 *  - Subline (wie de impersonator is + eventuele superadmin-origin
 *    + start-tijd)
 *  - "Stop shadow"-knop (alleen in user-shadow modus)
 *
 * Rendert niets als er geen shadow actief is.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@shared/stores/auth';
import { useImpersonationStore } from '@shared/stores/impersonation';
import styles from './ShadowIndicator.module.css';

export function ShadowIndicator() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const meta = useImpersonationStore((s) => s.meta);
  const loading = useImpersonationStore((s) => s.loading);
  const stop = useImpersonationStore((s) => s.stop);

  const startedAt = useMemo(() => {
    if (typeof meta.started_at !== 'string' || meta.started_at === '') return null;
    try {
      return new Date(meta.started_at).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  }, [meta.started_at]);

  if (meta.mode === null) return null;

  const heading = meta.mode === 'user-shadow'
    ? t('shadowBanner.userShadow.heading', { name: user?.display_name ?? '' })
    : t('shadowBanner.superadminTenantAdmin.heading');

  const subline = (() => {
    if (meta.mode === 'user-shadow') {
      if (meta.superadmin_origin !== null) {
        return t('shadowBanner.userShadow.sublineWithOrigin', {
          impersonator: meta.impersonator?.display_name ?? '',
          origin: meta.superadmin_origin.display_name,
        });
      }
      return t('shadowBanner.userShadow.subline', {
        impersonator: meta.impersonator?.display_name ?? '',
      });
    }
    return t('shadowBanner.superadminTenantAdmin.subline', {
      origin: meta.superadmin_origin?.display_name ?? '',
    });
  })();

  const canStop = meta.mode === 'user-shadow';

  async function handleStop() {
    try {
      await stop();
    } catch {
      /* error toont via impersonation.error in een toaster elders */
    }
  }

  return (
    <div className={styles.indicator} role="status">
      <div className={styles.text}>
        <strong className={styles.heading}>{heading}</strong>
        <span className={styles.subline}>
          {subline}
          {startedAt !== null && ' · ' + t('shadowBanner.startedAt', { time: startedAt })}
        </span>
      </div>
      {canStop && (
        <button
          type="button"
          className={styles.stop}
          disabled={loading}
          onClick={handleStop}
        >
          {t('shadowBanner.stop')}
        </button>
      )}
    </div>
  );
}
